import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSlidersH, faSave, faSync, faExpand, faCompress, faGlobe,
  faShip, faExclamationTriangle, faArrowLeft,
  faExternalLinkAlt, faAnchor, faRoute, faCalendarAlt, faMapMarkerAlt,
  faTimes, faBinoculars, faTable,
} from '@fortawesome/free-solid-svg-icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import FleetMapComponent from '../FleetMapComponent';
import DeliveryWidgetCard from './DeliveryWidgetCard';
import WatchlistVesselCard from './WatchlistVesselCard';
import WidgetPalette from './WidgetPalette';
import VesselWatchlistPanel from './VesselWatchlistPanel';
import VesselComplianceBadge from './VesselComplianceBadge';
import VesselRiskChips from './VesselRiskChips';
import VesselIntelligencePanel from './VesselIntelligencePanel';
import VesselOverviewGrid from './VesselOverviewGrid';
import { DEFAULT_WIDGET_ORDER } from './widgetDefinitions';
import { globeService } from '../../services/globeService';
import { useFlashMessage } from '../FlashMessage';
import axiosInstance from '../../utils/axiosConfig';
import { formatAmount } from '../../utils/amountFormat';
import { QUANTITY_DECIMALS, formatFigure } from '../../utils/precision';
import './ShipmentOverview.css';

// Throttle window for auto-refresh on tab visits / data changes.
// Manual "Refresh" button bypasses this. Tuned to balance freshness vs. NavAPI tokens.
const FLEET_REFRESH_THROTTLE_MS = 5 * 60 * 1000;

const CARD_ACCENTS = [
  { border: '#4a6b25', gradient: 'linear-gradient(135deg, #4a6b25 0%, #3d5a1f 100%)', light: '#eef3e4' },
  { border: '#7030a0', gradient: 'linear-gradient(135deg, #7030a0 0%, #5b2480 100%)', light: '#f3e8ff' },
  { border: '#0d9488', gradient: 'linear-gradient(135deg, #0d9488 0%, #0a7a70 100%)', light: '#e6faf8' },
  { border: '#d97706', gradient: 'linear-gradient(135deg, #d97706 0%, #b45f04 100%)', light: '#fef3e2' },
  { border: '#dc2626', gradient: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)', light: '#fee2e2' },
  { border: '#2563eb', gradient: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', light: '#dbeafe' },
  { border: '#059669', gradient: 'linear-gradient(135deg, #059669 0%, #047857 100%)', light: '#d1fae5' },
  { border: '#e11d48', gradient: 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)', light: '#ffe4eb' },
];

const getCardAccent = (index) => CARD_ACCENTS[index % CARD_ACCENTS.length];

// Strict UN/LOCODE format: 2 letters (country) + 3 letters/digits (port).
// Used to reject AIS-declared free-text destinations like "SK-JZ044.08"
// (a Chinese terminal berth code, NOT a UN/LOCODE for Slovakia) before
// hitting the backend resolver.
const UNLOCODE_PATTERN = /^[A-Z]{2}[A-Z0-9]{3}$/i;

const portCoordCache = {};

// Pull a resolvable UN/LOCODE out of a port field, however it was stored: the
// PORTS dropdown saves "1sp 1sb NAME, COUNTRY, UNLOCODE"; recaps may leave a bare
// code or "NAME, COUNTRY, UNLOCODE". Returns '' for AIS free-text / plain names.
const extractUnlocode = (val) => {
  if (!val) return '';
  const s = String(val).trim();
  const body = s.startsWith('1sp 1sb ') ? s.replace('1sp 1sb ', '') : s;
  if (body.includes(',')) {
    const last = body.split(',').pop().trim();
    if (UNLOCODE_PATTERN.test(last)) return last;
  }
  return UNLOCODE_PATTERN.test(s) ? s : '';
};

// Resolve a clean UN/LOCODE to a plot dot. Passes ?routing=1 so the backend
// only returns a genuine navigable port (real port_code from the local DB or
// SeaPortSearch); a Mapbox / country-centroid guess yields 404 -> null -> no
// dot. This keeps the port DOTS under the same "real port only" rule as the
// route LINES, so AIS free-text codes never plot a mislocated dot.
const resolvePortCoords = async (code) => {
  if (!code) return null;
  const trimmed = String(code).trim().toUpperCase();
  if (!UNLOCODE_PATTERN.test(trimmed)) return null;
  const cacheKey = `dot:${trimmed}`;
  if (portCoordCache[cacheKey]) return portCoordCache[cacheKey];
  try {
    const resp = await axiosInstance.get('/api/globe/resolve-port/', { params: { code: trimmed, routing: 1 } });
    const d = resp.data;
    if (d.lat != null && d.lon != null) {
      const result = { lat: d.lat, lon: d.lon, name: d.name || trimmed, country: d.country || '' };
      portCoordCache[cacheKey] = result;
      return result;
    }
  } catch (err) {
    console.warn(`Port resolution failed for ${trimmed}:`, err.message || err);
  }
  return null;
};

// Strip storage decoration off a free-text port field to recover a plain NAME
// the backend SeaPortSearch fallback can resolve: drop a leading "1sp 1sb "
// berth prefix and, if the value is "NAME, COUNTRY, CODE"-shaped, keep only the
// leading NAME segment (trailing berth codes / countries confuse the lookup).
const cleanPortName = (val) => {
  if (!val) return '';
  let s = String(val).trim();
  if (s.startsWith('1sp 1sb ')) s = s.replace('1sp 1sb ', '');
  if (s.includes(',')) s = s.split(',')[0].trim();
  return s;
};

// Resolve a free-text port NAME (not a UN/LOCODE) to coords via the backend's
// SeaPortSearch fallback. Passes ?routing=1 so only a genuine navigable port
// (real port_code) plots a dot; a Mapbox / country-centroid geocode yields
// 404 -> null -> no dot, so AIS free-text names/typos like "HAMRIAYH" no longer
// drop a mislocated dot. Cached by name in the same portCoordCache so a given
// name only hits the endpoint once per session.
const resolvePortByName = async (rawVal) => {
  const name = cleanPortName(rawVal);
  if (!name || name.replace(/\s/g, '').length < 3) return null;
  const cacheKey = `name:${name.toUpperCase()}`;
  if (portCoordCache[cacheKey]) return portCoordCache[cacheKey];
  try {
    const resp = await axiosInstance.get('/api/globe/resolve-port/', { params: { q: name, routing: 1 } });
    const d = resp.data;
    if (d.lat != null && d.lon != null) {
      const result = { lat: d.lat, lon: d.lon, name: d.name || name, country: d.country || '' };
      portCoordCache[cacheKey] = result;
      return result;
    }
  } catch (err) {
    console.warn(`Port name resolution failed for "${name}":`, err.message || err);
  }
  return null;
};

// Resolve a delivery/AIS port field to coords: try the clean UN/LOCODE fast path
// first, then fall back to the free-text NAME resolver for names/typos.
const resolvePortField = async (rawVal) => {
  const code = extractUnlocode(rawVal);
  if (code) return resolvePortCoords(code);
  return resolvePortByName(rawVal);
};

// Resolve a discharge port field to a CANONICAL UN/LOCODE for the routing engine.
// Passes ?routing=1 so the backend skips the Mapbox / country-centroid guess and
// only returns a genuine navigable port (real port_code from local DB or
// SeaPortSearch), else 404. Returns the resolved port_code string, or '' when the
// discharge cannot be resolved to a real port (→ we must NOT draw a route line).
// Cached per query in the shared portCoordCache under a distinct "route:" key so
// it never collides with the plot-dot resolution above.
const resolveDischargeRouteCode = async (rawVal) => {
  if (!rawVal) return '';
  const code = extractUnlocode(rawVal);
  const params = code ? { code } : { q: cleanPortName(rawVal) };
  if (!code && (!params.q || params.q.replace(/\s/g, '').length < 3)) return '';
  params.routing = 1;
  const cacheKey = `route:${(code || params.q).toUpperCase()}`;
  if (cacheKey in portCoordCache) return portCoordCache[cacheKey];
  try {
    const resp = await axiosInstance.get('/api/globe/resolve-port/', { params });
    const routeCode = resp.data?.port_code || '';
    portCoordCache[cacheKey] = routeCode;
    return routeCode;
  } catch (err) {
    // 404 (no navigable port) is the expected "unresolved" signal, not an error.
    portCoordCache[cacheKey] = '';
    return '';
  }
};

// Stable identifier for a vessel across cards / map markers / port dots.
// Deliveries take priority since a single vessel could appear via both
// a delivery and a watchlist entry; deliveryId wins so card hover and
// marker glow line up with the visible delivery card.
const getVesselKey = (v) => {
  if (!v) return null;
  if (v.deliveryId) return `d:${v.deliveryId}`;
  if (v.watchlistEntryId) return `w:${v.watchlistEntryId}`;
  if (v.imo) return `i:${v.imo}`;
  if (v.mmsi) return `m:${v.mmsi}`;
  return null;
};

// Resolve loading + discharging port dots for one vessel.
// DELIVERY vessels plot dots ONLY from the deal's typed port fields — AIS
// origin/destination is machine data about the VESSEL, not the deal, so it never
// plots a load/discharge dot (empty field → no dot, no route leg). WATCHLIST
// vessels have no delivery, so AIS is all they have: they keep the AIS fallback
// (their existing intended behaviour).
// Returns the same shape used by FleetMapComponent's port markers.
const enrichVesselPorts = async ({ load = '', disch = '' } = {}) => {
  // Load/discharge dots + the route line come ONLY from typed ports — the
  // deal's ports for a delivery, or the user-entered ports for a watchlist
  // vessel. AIS-declared origin/destination is deliberately NEVER used to
  // auto-plot a load/discharge; a watchlist vessel with no ports set shows just
  // its live-position marker until the user enters ports in the slide-out.
  // resolvePortField uses the clean UN/LOCODE fast path when present, else the
  // free-text NAME resolver (SeaPortSearch) so names/typos still plot a dot.
  const loadVal = load || '';
  const dischVal = disch || '';
  // The discharge is ALSO resolved to a canonical UN/LOCODE (routing=1) so the
  // route line can be drawn from the vessel's live position to the discharge
  // port CODE (NavAPI uses its own navigable coords) rather than to a possibly
  // mislocated dot. dischRouteCode is '' when no real port resolves → no line.
  const [loadResolved, dischResolved, dischRouteCode] = await Promise.all([
    resolvePortField(loadVal),
    resolvePortField(dischVal),
    resolveDischargeRouteCode(dischVal),
  ]);
  return [
    loadResolved
      ? { type: 'loading', name: loadResolved.name, lat: loadResolved.lat, lon: loadResolved.lon }
      : null,
    dischResolved
      ? {
          type: 'discharge',
          name: dischResolved.name,
          lat: dischResolved.lat,
          lon: dischResolved.lon,
          // Canonical port code for routing (empty when the discharge did not
          // resolve to a real navigable port).
          routeCode: dischRouteCode || '',
        }
      : (dischRouteCode
          // No plottable dot but a real port code still resolved: keep a
          // code-only discharge so the live→discharge line can still be drawn.
          ? { type: 'discharge', name: dischVal || '', lat: null, lon: null, routeCode: dischRouteCode }
          : null),
  ].filter(Boolean);
};

const mergeWithDefaults = (saved) => {
  if (!saved || saved.length === 0) return DEFAULT_WIDGET_ORDER;
  const savedIds = new Set(saved.map(w => w.id));
  const merged = [...saved];
  DEFAULT_WIDGET_ORDER.forEach(def => {
    if (!savedIds.has(def.id)) merged.push({ ...def, order: merged.length });
  });
  return merged;
};

const ShipmentOverviewTab = ({ deliveries = [], onOpenDelivery }) => {
  const history = useHistory();
  const { showSuccess, showError, showInfo } = useFlashMessage();
  // { 'default': [...widgets], deliveryId: [...widgets] }
  const [layoutMap, setLayoutMap] = useState({ default: DEFAULT_WIDGET_ORDER });
  const [loadedLayoutIds, setLoadedLayoutIds] = useState(new Set(['pending_default']));
  const [showPalette, setShowPalette] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [fleetPositions, setFleetPositions] = useState([]);
  // Vessels we asked the API to locate but which came back without a real AIS
  // fix. These are NOT plotted (no fake mid-country marker); they are listed
  // in a small "Position unavailable" section instead.
  // Shape: [{ key, vessel_name, imo, mmsi }]
  const [noPositionVessels, setNoPositionVessels] = useState([]);
  const [fleetLoading, setFleetLoading] = useState(false);
  const [fleetError, setFleetError] = useState(null);
  // Cross-element hover linking: when a card / vessel marker / port marker
  // is hovered, all elements sharing this key glow. Format produced by
  // getVesselKey(): "d:<deliveryId>" | "w:<watchlistEntryId>" | "i:<imo>" | "m:<mmsi>".
  const [hoveredVesselKey, setHoveredVesselKey] = useState(null);
  // One-shot fly-to trigger driven by card clicks. The map watches `ts` to
  // detect new requests (so clicking the same card twice still re-centers).
  const [flyTrigger, setFlyTrigger] = useState({ key: null, ts: 0 });
  // One-shot "show all" trigger for the toolbar button. Re-fits bounds on
  // every increment so the user can pull the map back to the full view at
  // any time after they've zoomed into a single vessel.
  const [fitAllTrigger, setFitAllTrigger] = useState(0);
  const [expandedDeliveryId, setExpandedDeliveryId] = useState(null);
  const [fullDeliveries, setFullDeliveries] = useState({});
  const [layoutSaving, setLayoutSaving] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistLoaded, setWatchlistLoaded] = useState(false);
  // Which set of cards shows under the map: delivery vessels (with R&C widgets)
  // watchlist vessels (identity + ports + live AIS), or the spreadsheet-layout
  // overview grid. 'deliveries' | 'watchlist' | 'grid'.
  const [cardView, setCardView] = useState('deliveries');
  // Watchlist entry currently being removed from the cards grid (for its spinner).
  const [removingWatchlistId, setRemovingWatchlistId] = useState(null);
  // Watchlist card expanded to show its full Risk & Compliance card (like the
  // delivery cards expand). Only one open at a time.
  const [expandedWatchlistId, setExpandedWatchlistId] = useState(null);

  // ── AIS vessel discovery (search) overlay ──────────────────────────
  // Ad-hoc search hits plotted as distinct markers, separate from the tracked
  // fleet. Everything here is user-driven: nothing fires automatically (the
  // endpoints bill 1 token per vessel returned).
  // Vessel Discovery UI is removed from view for now (panel + sidebar tab).
  // The map plumbing below stays wired with dormant defaults so the feature
  // can be restored later by re-adding VesselDiscoveryPanel + its handlers
  // (see git history). Values stay; setters that only the panel handlers used
  // are dropped to keep lint clean.
  const [discoveredVessels] = useState([]);
  const [discoveryCenter] = useState(null);       // {lat,lon}
  const [discoveryRadiusKm] = useState(null);
  const [discoveryPickActive, setDiscoveryPickActive] = useState(false);
  const [discoveryPickedPoint, setDiscoveryPickedPoint] = useState(null); // {lat,lon}
  const [discoveryFocus] = useState(null);         // {lat,lon,ts}
  const [discoveryFitTrigger] = useState(0);
  // Slide-out sidebar over the map hosting the watchlist + vessel discovery.
  const [mapSidebarOpen, setMapSidebarOpen] = useState(false);
  const [mapSidebarTab, setMapSidebarTab] = useState('watchlist'); // 'watchlist' | 'discover'

  // "Pick on map" needs the map unobstructed: collapse the sidebar while the
  // pick is armed, and slide it back open (on the Discover tab) once a point
  // lands so the trader can tweak radius/filters and hit Search.
  useEffect(() => {
    if (discoveryPickActive) setMapSidebarOpen(false);
  }, [discoveryPickActive]);
  useEffect(() => {
    if (discoveryPickedPoint) {
      setMapSidebarTab('discover');
      setMapSidebarOpen(true);
    }
  }, [discoveryPickedPoint]);

  const lastFleetRefreshRef = useRef(0);
  const fleetRefreshLockRef = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Load user's default layout on mount
  useEffect(() => {
    const loadDefault = async () => {
      try {
        const res = await axiosInstance.get('/api/deliveries/overview-layout/');
        const saved = res.data?.layout_config?.widgets;
        setLayoutMap(prev => ({ ...prev, default: mergeWithDefaults(saved) }));
      } catch (err) {
        // keep DEFAULT_WIDGET_ORDER
      }
      setLoadedLayoutIds(prev => { const n = new Set(prev); n.delete('pending_default'); n.add('default'); return n; });
    };
    loadDefault();
  }, []);

  // Load per-delivery layout when a card is expanded
  useEffect(() => {
    if (!expandedDeliveryId) return;
    const key = String(expandedDeliveryId);
    if (loadedLayoutIds.has(key)) return;
    setLoadedLayoutIds(prev => new Set(prev).add(key));

    const loadDeliveryLayout = async () => {
      try {
        const res = await axiosInstance.get(`/api/deliveries/${expandedDeliveryId}/overview-layout/`);
        const saved = res.data?.layout_config?.widgets;
        if (saved && saved.length > 0) {
          setLayoutMap(prev => ({ ...prev, [key]: mergeWithDefaults(saved) }));
        }
        // if no saved per-delivery layout, it falls back to default (no entry in map)
      } catch (err) {
        // fall back to default
      }
    };
    loadDeliveryLayout();
  }, [expandedDeliveryId, loadedLayoutIds]);

  // Get widget config for a specific delivery, falling back to default
  const getWidgetConfig = useCallback((deliveryId) => {
    const key = String(deliveryId);
    return layoutMap[key] || layoutMap.default || DEFAULT_WIDGET_ORDER;
  }, [layoutMap]);

  // ─── Vessel Watchlist + Fleet Refresh ────────────────────────────
  // Strategy (token-conservative):
  //   • Initial mount + deliveries change: 1 batch call covering both
  //     delivery vessels AND watchlist vessels (max 2 NavAPI requests).
  //   • Auto-refresh is throttled to 5 min; manual "Refresh" bypasses it.
  //   • Add vessel: single-position fetch and merge (1 token).
  //   • Remove vessel: local filter only (0 tokens).

  // Load watchlist on mount.
  useEffect(() => {
    let cancelled = false;
    globeService.getWatchlist()
      .then(items => { if (!cancelled) setWatchlist(items); })
      .catch(err => console.warn('Could not load watchlist:', err.message || err))
      .finally(() => { if (!cancelled) setWatchlistLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const refreshFleet = useCallback(async ({ force = false, silent = false } = {}) => {
    if (fleetRefreshLockRef.current) {
      if (!silent) showInfo('Refresh already in progress...');
      return;
    }
    if (!force && (Date.now() - lastFleetRefreshRef.current) < FLEET_REFRESH_THROTTLE_MS) {
      if (!silent) {
        const secsLeft = Math.ceil(
          (FLEET_REFRESH_THROTTLE_MS - (Date.now() - lastFleetRefreshRef.current)) / 1000
        );
        const mins = Math.floor(secsLeft / 60);
        const secs = secsLeft % 60;
        const remaining = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
        showInfo(`Vessel positions were just refreshed. Please wait ${remaining} or use Refresh to force.`);
      }
      return;
    }

    // Accept variable-width identifiers because NavAPI itself returns 7 or 8
    // digit IMOs depending on the vessel.
    const isValidImo = (v) => /^\d{3,12}$/.test((v || '').trim());
    const isValidMmsi = (v) => /^\d{3,14}$/.test((v || '').trim());

    const imoSet = new Set();
    const mmsiSet = new Set();
    const meta = {}; // identifier -> { delivery?, watchlistEntryId? }

    deliveries.forEach(d => {
      if (d.vessel_imo_number && isValidImo(d.vessel_imo_number)) {
        const imo = d.vessel_imo_number.trim();
        imoSet.add(imo);
        meta[imo] = { ...(meta[imo] || {}), delivery: d };
      } else if (d.vessel_mmsi_number && isValidMmsi(d.vessel_mmsi_number)) {
        const mmsi = d.vessel_mmsi_number.trim();
        mmsiSet.add(mmsi);
        meta[mmsi] = { ...(meta[mmsi] || {}), delivery: d };
      }
    });

    watchlist.forEach(w => {
      if (w.imo && isValidImo(w.imo)) {
        imoSet.add(w.imo);
        meta[w.imo] = { ...(meta[w.imo] || {}), watchlistEntryId: w.id, watchlistEntry: w };
      } else if (w.mmsi && isValidMmsi(w.mmsi)) {
        mmsiSet.add(w.mmsi);
        meta[w.mmsi] = { ...(meta[w.mmsi] || {}), watchlistEntryId: w.id, watchlistEntry: w };
      }
    });

    const imoList = Array.from(imoSet);
    const mmsiList = Array.from(mmsiSet);

    if (imoList.length === 0 && mmsiList.length === 0) {
      setFleetPositions([]);
      setNoPositionVessels([]);
      // Do NOT stamp lastFleetRefreshRef here: no NavAPI request was made, so
      // this must not consume the throttle window. The tab often mounts with
      // `deliveries` still [], so the first auto-refresh hits this empty branch;
      // stamping the clock here would throttle-block the real auto-refresh that
      // fires the instant `deliveries` populates, leaving the map empty until a
      // manual Refresh.
      if (force && !silent) {
        showInfo('No vessels tracked yet. Add a delivery vessel or a watchlist vessel to see it on the map.');
      }
      return;
    }

    fleetRefreshLockRef.current = true;
    setFleetLoading(true);
    setFleetError(null);

    try {
      const { positions } = await globeService.getFleetPositionsDetailed(
        imoList.length > 0 ? imoList : null,
        mmsiList.length > 0 ? mmsiList : null
      );

      // Identify requested vessels that came back without a plottable fix.
      // The backend drops no-fix vessels from `positions` (never plots a fake
      // coordinate), so we diff the requested identifiers against what we got
      // back and pull friendly names from the delivery/watchlist meta.
      const plottedIds = new Set();
      positions.forEach(pos => {
        if (pos.imo) plottedIds.add(pos.imo);
        if (pos.mmsi) plottedIds.add(pos.mmsi);
      });
      const unavailable = [];
      [...imoList, ...mmsiList].forEach(id => {
        if (plottedIds.has(id)) return;
        const m = meta[id] || {};
        const md = m.delivery;
        const w = watchlist.find(x => x.imo === id || x.mmsi === id);
        const name = md?.vessel_name || w?.vessel_name || '';
        const isImo = imoList.includes(id);
        unavailable.push({
          key: `np:${id}`,
          vessel_name: name,
          imo: isImo ? id : (md?.vessel_imo_number || w?.imo || ''),
          mmsi: isImo ? (md?.vessel_mmsi_number || w?.mmsi || '') : id,
        });
      });
      setNoPositionVessels(unavailable);

      const enriched = positions.map(pos => {
        const m = meta[pos.imo] || meta[pos.mmsi] || {};
        const md = m.delivery;
        const we = m.watchlistEntry;
        return {
          ...pos,
          deliveryId: md?.id,
          deliveryNumber: md?.delivery_unique_number,
          watchlistEntryId: m.watchlistEntryId,
          product: md?.sales_product_category || '',
          // The intended discharge. Deliveries keep their existing AIS-destination
          // fallback; watchlist vessels use ONLY the user-entered discharge (no
          // AIS), so an un-set watchlist vessel is never flagged "discharge
          // unresolved" for a port the user never chose.
          dischargeIntent: md
            ? (md.discharging_port_location || pos.destination || '')
            : (we?.discharging_port_location || ''),
          // Sanctions/compliance-driven marker colour. Defaults keep every
          // vessel GREEN until the batch screen resolves (never a red flash).
          complianceFlagged: false,
          sanctionsStatus: 'unknown',
          ports: [],
        };
      });
      setFleetPositions(enriched);

      // Resolve loading + discharging dots per vessel from TYPED ports only:
      // the deal's ports for a delivery, the user-entered ports for a watchlist
      // vessel. Neither auto-fills from AIS origin/destination.
      // In parallel, batch-screen every hull once so markers can be coloured
      // green (cleared) / red (sanctioned or not cleared) — one call keeps
      // Kpler/NavAPI token use flat and reuses the 6h server-side cache.
      const compliancePromise = globeService.getVesselComplianceBatch(imoList);
      const portsByVesselKey = {};
      await Promise.all(enriched.map(async v => {
        const m = meta[v.imo] || meta[v.mmsi] || {};
        const md = m.delivery;
        const we = m.watchlistEntry;
        const src = md
          ? { load: md.loading_port_location || '', disch: md.discharging_port_location || '' }
          : { load: we?.loading_port_location || '', disch: we?.discharging_port_location || '' };
        portsByVesselKey[getVesselKey(v)] = await enrichVesselPorts(src);
      }));
      let complianceByImo = {};
      try {
        const compliance = await compliancePromise;
        complianceByImo = compliance?.results || {};
      } catch (e) {
        complianceByImo = {};
      }
      setFleetPositions(prev => prev.map(v => {
        const c = v.imo ? complianceByImo[v.imo] : null;
        const flagged = !!c && (c.sanctions_status === 'flagged' || c.warning === true);
        return {
          ...v,
          ports: portsByVesselKey[getVesselKey(v)] || v.ports || [],
          // Only overwrite when this batch actually screened the hull; otherwise
          // preserve any status a concurrent single-vessel add already resolved
          // (a vessel added mid-refresh won't be in this batch's imoList).
          complianceFlagged: c ? flagged : v.complianceFlagged,
          sanctionsStatus: c ? (c.sanctions_status || 'unknown') : v.sanctionsStatus,
        };
      }));

      lastFleetRefreshRef.current = Date.now();
      if (force && !silent) {
        const plottedCount = enriched.length;
        const unavailableCount = unavailable.length;
        if (plottedCount === 0 && unavailableCount > 0) {
          // We have vessels to track but none currently report a position.
          showInfo(`Positions temporarily unavailable for ${unavailableCount} vessel${unavailableCount === 1 ? '' : 's'}. They will appear once a recent fix is received.`);
        } else if (unavailableCount > 0) {
          showSuccess(`Updated ${plottedCount} vessel position${plottedCount === 1 ? '' : 's'}. ${unavailableCount} awaiting a recent fix.`);
        } else {
          showSuccess(`Updated ${plottedCount} vessel position${plottedCount === 1 ? '' : 's'}.`);
        }
      }
    } catch (err) {
      console.error('Fleet position fetch error:', err);
      setFleetError('Unable to load vessel positions');
      if (!silent) showError(err.message || 'Unable to load vessel positions');
    } finally {
      setFleetLoading(false);
      fleetRefreshLockRef.current = false;
    }
  }, [deliveries, watchlist, showInfo, showSuccess, showError]);

  // Stable ref so the effect below doesn't re-fire on every refreshFleet identity change.
  const refreshFleetRef = useRef(refreshFleet);
  refreshFleetRef.current = refreshFleet;

  // Trigger fleet refresh once watchlist has loaded, and on every deliveries change.
  // Watchlist mutations (add/remove) intentionally do NOT trigger a full refresh --
  // the dedicated handlers below do targeted single-vessel updates instead.
  // Silent so throttle-blocked auto refreshes don't pester the user.
  useEffect(() => {
    if (!watchlistLoaded) return;
    refreshFleetRef.current({ force: false, silent: true });
  }, [watchlistLoaded, deliveries]);

  const handleManualRefresh = useCallback(() => {
    refreshFleet({ force: true });
  }, [refreshFleet]);

  const handleWatchlistAdded = useCallback(async (entry, { isDuplicate } = {}) => {
    setWatchlist(prev => {
      if (prev.some(w => w.id === entry.id)) return prev;
      return [entry, ...prev];
    });

    if (isDuplicate) {
      showInfo(`${entry.vessel_name} is already on your watchlist.`);
    } else {
      showSuccess(`${entry.vessel_name} added to watchlist.`);
    }

    // Avoid duplicate work if the vessel is already on the map (e.g., it
    // also belongs to a delivery, or we already fetched it earlier).
    const alreadyOnMap = fleetPositions.some(p =>
      (entry.imo && p.imo && p.imo === entry.imo) ||
      (entry.mmsi && p.mmsi && p.mmsi === entry.mmsi)
    );
    if (alreadyOnMap) {
      setFleetPositions(prev => prev.map(p => {
        const matches = (entry.imo && p.imo === entry.imo) || (entry.mmsi && p.mmsi === entry.mmsi);
        return matches ? { ...p, watchlistEntryId: entry.id } : p;
      }));
      return;
    }

    try {
      const pos = await globeService.getVesselPosition(entry.imo || null, entry.mmsi || null);
      if (!pos) {
        // No plottable fix -- list it under "Position unavailable" instead of
        // dropping a marker in the middle of a country.
        setNoPositionVessels(prev => {
          const key = `np:${entry.imo || entry.mmsi}`;
          if (prev.some(v => v.key === key)) return prev;
          return [...prev, { key, vessel_name: entry.vessel_name, imo: entry.imo || '', mmsi: entry.mmsi || '' }];
        });
        showInfo(`No recent AIS position available for ${entry.vessel_name}.`);
        return;
      }
      // Resolve ports inline so any dots appear immediately, matching what the
      // next refreshFleet would produce. A freshly-added watchlist vessel has no
      // typed ports yet, so this yields NO load/discharge dots or route — only
      // the live-position marker — until the user sets ports in the slide-out.
      const ports = await enrichVesselPorts({
        load: entry.loading_port_location || '',
        disch: entry.discharging_port_location || '',
      });
      // Screen the new hull so its marker colours correctly straight away
      // (green cleared / red sanctioned-or-not-cleared) instead of waiting for
      // the next throttled refresh. Non-fatal — stays green if screening fails.
      let complianceFlagged = false;
      let sanctionsStatus = 'unknown';
      if (entry.imo) {
        try {
          const c = await globeService.getVesselComplianceBatch([entry.imo]);
          const r = c?.results?.[entry.imo];
          if (r) {
            complianceFlagged = r.sanctions_status === 'flagged' || r.warning === true;
            sanctionsStatus = r.sanctions_status || 'unknown';
          }
        } catch (e) { /* keep default green */ }
      }
      setFleetPositions(prev => [
        ...prev,
        {
          ...pos,
          watchlistEntryId: entry.id,
          dischargeIntent: entry.discharging_port_location || '',
          ports,
          complianceFlagged,
          sanctionsStatus,
        },
      ]);
      // It now has a real fix -- make sure it isn't also listed as unavailable.
      setNoPositionVessels(prev => prev.filter(v => v.key !== `np:${entry.imo || entry.mmsi}`));
    } catch (err) {
      console.warn('Could not fetch position for newly added watchlist vessel:', err.message || err);
      showError(`${entry.vessel_name} added but its position could not be loaded: ${err.message || 'unknown error'}`);
    }
  }, [fleetPositions, showSuccess, showInfo, showError]);

  const handleWatchlistRemoved = useCallback((entryId, { vesselName } = {}) => {
    const removed = watchlist.find(w => w.id === entryId);
    setWatchlist(prev => prev.filter(w => w.id !== entryId));
    setFleetPositions(prev => prev.filter(p => {
      if (p.watchlistEntryId !== entryId) return true;
      return Boolean(p.deliveryId);
    }));
    // Drop it from the "Position unavailable" list too. Only drop if no
    // delivery still references the same hull (a delivery vessel keeps it).
    if (removed) {
      const stillTracked = deliveries.some(d =>
        (removed.imo && d.vessel_imo_number === removed.imo) ||
        (removed.mmsi && d.vessel_mmsi_number === removed.mmsi)
      );
      if (!stillTracked) {
        const key = `np:${removed.imo || removed.mmsi}`;
        setNoPositionVessels(prev => prev.filter(v => v.key !== key));
      }
    }
    if (vesselName) showSuccess(`${vesselName} removed from watchlist.`);
  }, [watchlist, deliveries, showSuccess]);

  // A watchlist vessel's load/discharge ports were edited in the slide-out.
  // Reflect them in the watchlist list (drives the card + the next refresh) and
  // re-resolve just this vessel's dots + route line so the map updates without
  // waiting for the throttled full refresh.
  const handleWatchlistPortsUpdated = useCallback(async (updated) => {
    if (!updated || !updated.id) return;
    setWatchlist(prev => prev.map(w => (w.id === updated.id ? { ...w, ...updated } : w)));
    // Only re-plot the vessel's map dots/route from watchlist ports if it is NOT
    // also a delivery: a delivery owns its hull's plot (the deal's typed ports,
    // never AIS), matching the delivery-wins invariant used in handleWatchlistRemoved.
    const target = fleetPositions.find(p => p.watchlistEntryId === updated.id && !p.deliveryId);
    if (!target) return; // no live fix (or delivery-owned); next refresh resolves it
    try {
      const ports = await enrichVesselPorts({
        load: updated.loading_port_location || '',
        disch: updated.discharging_port_location || '',
      });
      setFleetPositions(prev => prev.map(p =>
        (p.watchlistEntryId === updated.id && !p.deliveryId)
          ? { ...p, ports, dischargeIntent: updated.discharging_port_location || p.destination || '' }
          : p
      ));
    } catch (err) {
      console.warn('Could not re-resolve watchlist ports:', err.message || err);
    }
  }, [fleetPositions]);

  // Remove handler for the watchlist CARDS grid. Unlike the slide-out panel
  // (which deletes then calls onRemoved), the card has no delete of its own, so
  // this owns the API call and then reuses handleWatchlistRemoved for state.
  const handleWatchlistCardRemove = useCallback(async (entry) => {
    if (!entry || !entry.id) return;
    setRemovingWatchlistId(entry.id);
    try {
      await globeService.removeWatchlistVessel(entry.id);
      handleWatchlistRemoved(entry.id, { vesselName: entry.vessel_name });
    } catch (err) {
      showError(err.message || 'Could not remove vessel');
    } finally {
      setRemovingWatchlistId(null);
    }
  }, [handleWatchlistRemoved, showError]);

  // ── AIS discovery handlers ─────────────────────────────────────────
  // (Panel handlers removed with the Discovery UI; only the inert map-pick
  // handler remains so FleetMapComponent's onDiscoveryPick prop stays wired.
  // Pick mode can no longer be armed without the panel.)
  const handleDiscoveryPick = useCallback((point) => {
    setDiscoveryPickedPoint(point);
    setDiscoveryPickActive(false);
  }, []);

  const fullDeliveriesRef = React.useRef(fullDeliveries);
  fullDeliveriesRef.current = fullDeliveries;

  const fetchFullDelivery = useCallback(async (deliveryId) => {
    if (fullDeliveriesRef.current[deliveryId]) return;
    try {
      const res = await axiosInstance.get(`/api/deliveries/${deliveryId}/`);
      setFullDeliveries(prev => ({ ...prev, [deliveryId]: res.data }));
    } catch (err) {
      console.error('Error fetching delivery details:', err);
    }
  }, []);

  const handleToggleExpand = useCallback((deliveryId) => {
    setExpandedDeliveryId(prev => {
      if (prev === deliveryId) return null;
      fetchFullDelivery(deliveryId);
      return deliveryId;
    });
    setIsEditMode(false);
    setShowPalette(false);
  }, [fetchFullDelivery]);

  const handleToggleWidget = useCallback((deliveryId, widgetId) => {
    const key = String(deliveryId);
    setLayoutMap(prev => {
      const current = prev[key] || prev.default || DEFAULT_WIDGET_ORDER;
      const updated = current.map(w => w.id === widgetId ? { ...w, visible: !w.visible } : w);
      return { ...prev, [key]: updated };
    });
  }, []);

  const handleDragEnd = useCallback((deliveryId, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const key = String(deliveryId);
    setLayoutMap(prev => {
      const current = prev[key] || prev.default || DEFAULT_WIDGET_ORDER;
      const oldIdx = current.findIndex(w => w.id === active.id);
      const newIdx = current.findIndex(w => w.id === over.id);
      const updated = arrayMove(current, oldIdx, newIdx).map((w, i) => ({ ...w, order: i }));
      return { ...prev, [key]: updated };
    });
  }, []);

  const layoutMapRef = React.useRef(layoutMap);
  layoutMapRef.current = layoutMap;

  const handleSaveAndClose = useCallback(async (deliveryId) => {
    setLayoutSaving(true);
    const key = String(deliveryId);
    const config = layoutMapRef.current[key] || layoutMapRef.current.default || DEFAULT_WIDGET_ORDER;
    try {
      await axiosInstance.put(`/api/deliveries/${deliveryId}/overview-layout/`, {
        layout_config: { widgets: config },
        view_mode: 'command_center',
      });
      setIsEditMode(false);
      setShowPalette(false);
    } catch (err) {
      console.error('Error saving layout:', err);
      alert('Failed to save layout. Please try again.');
    } finally {
      setLayoutSaving(false);
    }
  }, []);

  // Count vessels actually showing on the map (delivery + watchlist combined).
  const vesselsWithPositions = useMemo(() => fleetPositions.length, [fleetPositions]);

  const formatDate = (val) => {
    if (!val) return '--';
    try { return new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); }
    catch { return '--'; }
  };

  // Live AIS position per delivery, so the card can show the vessel's
  // AIS-declared ETA (distinct from the planned delivery ETA) etc.
  const positionByDeliveryId = useMemo(() => {
    const map = {};
    fleetPositions.forEach(p => { if (p.deliveryId != null) map[p.deliveryId] = p; });
    return map;
  }, [fleetPositions]);

  // Plotted vessels whose discharge port could NOT be resolved to a real
  // navigable UN/LOCODE (so no route line is drawn — we never plot a wrong
  // line). Only counts vessels that HAVE a live position and an intended
  // discharge; if the discharge resolved to a code, a line is (or will be)
  // drawn and it is not listed here. Surfaced with the same quiet chip pattern
  // as "Position unavailable" rather than as an error.
  const unresolvedDischargeVessels = useMemo(() => {
    const out = [];
    fleetPositions.forEach(v => {
      if (v.latitude == null || v.longitude == null) return;
      if (!v.dischargeIntent) return;
      const dischargeHasCode = (v.ports || []).some(p => p.type === 'discharge' && p.routeCode);
      if (dischargeHasCode) return;
      out.push({
        key: `ud:${getVesselKey(v) || v.imo || v.mmsi}`,
        vessel_name: v.ship_name || '',
        discharge: v.dischargeIntent,
      });
    });
    return out;
  }, [fleetPositions]);

  // "12 Aug 14:30" from an ISO timestamp; '' when missing/invalid so the
  // caller can hide the field (no "--" spam for AIS values we simply lack).
  const formatDateTime = (val) => {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  // Compact "4d 6h" / "2d" / "5h" elapsed since an ISO timestamp; '' for
  // missing/invalid/future so dwell fields self-hide.
  const durationSince = (val) => {
    if (!val) return '';
    const then = new Date(val);
    if (isNaN(then.getTime())) return '';
    const ms = Date.now() - then.getTime();
    if (ms < 0) return '';
    const totalHours = Math.floor(ms / 3600000);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    if (hours > 0) return `${hours}h`;
    return '<1h';
  };

  return (
    <div className="so-container">
      <div className="so-toolbar">
        <div className="so-toolbar-left">
          <span className="so-toolbar-count">
            <FontAwesomeIcon icon={faShip} /> {vesselsWithPositions} vessel{vesselsWithPositions !== 1 ? 's' : ''} tracked
            {' / '}{deliveries.length} deliver{deliveries.length !== 1 ? 'ies' : 'y'}
          </span>
          {fleetLoading && <span className="so-toolbar-loading">Loading positions...</span>}
          {fleetError && (
            <span className="so-toolbar-error">
              <FontAwesomeIcon icon={faExclamationTriangle} /> {fleetError}
            </span>
          )}
        </div>
        <div className="so-toolbar-right">
          <button
            className="so-toolbar-btn"
            onClick={() => setFitAllTrigger(Date.now())}
            disabled={fleetPositions.length === 0}
            title="Show all vessels in view"
          >
            <FontAwesomeIcon icon={faGlobe} />
            <span className="so-toolbar-btn-label">Show All</span>
          </button>
          <button
            className="so-toolbar-btn"
            onClick={handleManualRefresh}
            disabled={fleetLoading}
            title="Refresh vessel positions"
          >
            <FontAwesomeIcon icon={faSync} spin={fleetLoading} />
            <span className="so-toolbar-btn-label">Refresh</span>
          </button>
          <button className="so-toolbar-btn" onClick={() => setMapExpanded(!mapExpanded)} title={mapExpanded ? 'Collapse map' : 'Expand map'}>
            <FontAwesomeIcon icon={mapExpanded ? faCompress : faExpand} />
          </button>
        </div>
      </div>

      <div className={`so-map-section ${mapExpanded ? 'so-map-expanded' : ''}`}>
        <FleetMapComponent
          vessels={fleetPositions}
          hoveredVesselKey={hoveredVesselKey}
          flyTrigger={flyTrigger}
          fitAllTrigger={fitAllTrigger}
          onHoverVessel={setHoveredVesselKey}
          onVesselClick={(vessel) => {
            const key = getVesselKey(vessel);
            if (key) {
              setHoveredVesselKey(key);
              setFlyTrigger({ key, ts: Date.now() });
            }
            if (vessel.deliveryId) handleToggleExpand(vessel.deliveryId);
          }}
          discoveredVessels={discoveredVessels}
          discoveryCenter={discoveryCenter}
          discoveryRadiusKm={discoveryRadiusKm}
          discoveryPickActive={discoveryPickActive}
          discoveryFocus={discoveryFocus}
          discoveryFitTrigger={discoveryFitTrigger}
          onDiscoveryPick={handleDiscoveryPick}
        />

        <button
          className={`so-map-sidebar-toggle ${mapSidebarOpen ? 'so-map-sidebar-toggle-open' : ''}`}
          onClick={() => setMapSidebarOpen(!mapSidebarOpen)}
          title={mapSidebarOpen ? 'Close vessel panel' : 'Watchlist & vessel discovery'}
        >
          <FontAwesomeIcon icon={mapSidebarOpen ? faTimes : faBinoculars} />
          {!mapSidebarOpen && <span className="so-map-sidebar-toggle-label">Vessels</span>}
        </button>

        <div className={`so-map-sidebar ${mapSidebarOpen ? 'so-map-sidebar-open' : ''}`}>
          <div className="so-map-sidebar-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={mapSidebarTab === 'watchlist'}
              className={`so-map-sidebar-tab ${mapSidebarTab === 'watchlist' ? 'so-map-sidebar-tab-active' : ''}`}
              onClick={() => setMapSidebarTab('watchlist')}
            >
              <FontAwesomeIcon icon={faShip} />
              Watchlist{watchlist.length > 0 ? ` (${watchlist.length})` : ''}
            </button>
          </div>
          <div className="so-map-sidebar-body">
            {mapSidebarTab === 'watchlist' && (
              <VesselWatchlistPanel
                watchlist={watchlist}
                hoveredVesselKey={hoveredVesselKey}
                onHoverVessel={setHoveredVesselKey}
                onCardClick={(entry) => {
                  // Fly to the vessel without leaving it permanently highlighted
                  // (that stuck highlight was stamping its port labels on the map).
                  setFlyTrigger({ key: `w:${entry.id}`, ts: Date.now() });
                }}
                onAdded={handleWatchlistAdded}
                onRemoved={handleWatchlistRemoved}
                onPortsUpdated={handleWatchlistPortsUpdated}
              />
            )}
          </div>
        </div>
      </div>

      {noPositionVessels.length > 0 && (
        <div className="so-nopos-section">
          <div className="so-nopos-header">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="so-nopos-icon" />
            <span className="so-nopos-title">
              Position unavailable ({noPositionVessels.length})
            </span>
            <span className="so-nopos-subtitle">
              Tracked, but no recent AIS fix to plot yet
            </span>
          </div>
          <div className="so-nopos-list">
            {noPositionVessels.map(v => (
              <div key={v.key} className="so-nopos-chip" title="No recent position from AIS">
                <FontAwesomeIcon icon={faShip} className="so-nopos-chip-icon" />
                <span className="so-nopos-chip-name">{v.vessel_name || 'Unknown vessel'}</span>
                <span className="so-nopos-chip-id">
                  {v.imo ? `IMO ${v.imo}` : v.mmsi ? `MMSI ${v.mmsi}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {unresolvedDischargeVessels.length > 0 && (
        <div className="so-nopos-section">
          <div className="so-nopos-header">
            <FontAwesomeIcon icon={faRoute} className="so-nopos-icon" />
            <span className="so-nopos-title">
              Discharge port unresolved ({unresolvedDischargeVessels.length})
            </span>
            <span className="so-nopos-subtitle">
              Plotted, but the discharge port could not be matched to a navigable port — no route line shown
            </span>
          </div>
          <div className="so-nopos-list">
            {unresolvedDischargeVessels.map(v => (
              <div key={v.key} className="so-nopos-chip" title="Discharge port could not be resolved to a navigable UN/LOCODE">
                <FontAwesomeIcon icon={faShip} className="so-nopos-chip-icon" />
                <span className="so-nopos-chip-name">{v.vessel_name || 'Unknown vessel'}</span>
                {v.discharge && (
                  <span className="so-nopos-chip-id">→ {v.discharge}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="so-divider so-view-divider">
        <div className="so-divider-line" />
        <div className="so-view-toggle" role="tablist" aria-label="Card view">
          <button
            type="button"
            role="tab"
            aria-selected={cardView === 'deliveries'}
            className={`so-view-toggle-btn ${cardView === 'deliveries' ? 'so-view-toggle-btn-active' : ''}`}
            onClick={() => setCardView('deliveries')}
          >
            <FontAwesomeIcon icon={faShip} /> Deliveries
            <span className="so-view-toggle-count">{deliveries.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={cardView === 'watchlist'}
            className={`so-view-toggle-btn ${cardView === 'watchlist' ? 'so-view-toggle-btn-active' : ''}`}
            onClick={() => setCardView('watchlist')}
          >
            <FontAwesomeIcon icon={faBinoculars} /> Watchlist
            <span className="so-view-toggle-count">{watchlist.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={cardView === 'grid'}
            className={`so-view-toggle-btn ${cardView === 'grid' ? 'so-view-toggle-btn-active' : ''}`}
            onClick={() => setCardView('grid')}
          >
            <FontAwesomeIcon icon={faTable} /> Overview Grid
            <span className="so-view-toggle-count">{deliveries.length}</span>
          </button>
        </div>
        <div className="so-divider-line" />
      </div>

      {cardView === 'grid' && (
        <VesselOverviewGrid
          deliveries={deliveries}
          onOpenDelivery={onOpenDelivery}
          hoveredVesselKey={hoveredVesselKey}
          onHoverVessel={setHoveredVesselKey}
        />
      )}

      {cardView === 'watchlist' && (
        <div className={`so-cards-grid ${expandedWatchlistId ? 'so-cards-focus-mode' : ''}`}>
          {watchlist.map((entry) => {
            const isExpanded = expandedWatchlistId === entry.id;
            if (expandedWatchlistId && !isExpanded) return null;
            const livePos = fleetPositions.find(p => p.watchlistEntryId === entry.id) || null;
            // For a hull that is ALSO a delivery, its live marker key is
            // `d:<deliveryId>`; align the card's fly/hover key with the live
            // position so clicking and hovering drive the same marker + route.
            const cardKey = livePos ? getVesselKey(livePos) : `w:${entry.id}`;
            return (
              <WatchlistVesselCard
                key={entry.id}
                entry={entry}
                livePos={livePos}
                vesselKey={cardKey}
                isHighlighted={hoveredVesselKey === cardKey}
                isExpanded={isExpanded}
                onHover={setHoveredVesselKey}
                onCardClick={() => {
                  // Fly to the vessel + toggle its R&C card. Deliberately does
                  // NOT persist a highlight (matches delivery card clicks), so
                  // the vessel + its ports aren't left permanently labelled.
                  setFlyTrigger({ key: cardKey, ts: Date.now() });
                  setExpandedWatchlistId(prev => (prev === entry.id ? null : entry.id));
                }}
                onCollapse={() => setExpandedWatchlistId(null)}
                onRemove={handleWatchlistCardRemove}
                removing={removingWatchlistId === entry.id}
              />
            );
          })}
          {watchlist.length === 0 && (
            <div className="so-empty-state">
              <FontAwesomeIcon icon={faBinoculars} className="so-empty-icon" />
              <h3>No watchlist vessels</h3>
              <p>Open the Watchlist panel on the map (top-right) to add vessels to monitor, then set their load &amp; discharge ports to plot routes.</p>
            </div>
          )}
        </div>
      )}

      {cardView === 'deliveries' && (
      <div className={`so-cards-grid ${expandedDeliveryId ? 'so-cards-focus-mode' : ''}`}>
        {deliveries.map((delivery, idx) => {
          const isExpanded = expandedDeliveryId === delivery.id;
          const isHidden = expandedDeliveryId && !isExpanded;
          const cardKey = `d:${delivery.id}`;
          const isHighlighted = hoveredVesselKey === cardKey;
          const fullData = fullDeliveries[delivery.id] || delivery;
          const accent = getCardAccent(idx);

          // Live AIS detail for this delivery's vessel (already fetched into
          // fleetPositions). Used to show the vessel's AIS-declared ETA next to
          // the planned ETA so a trader can see if she's running late vs plan.
          const livePos = positionByDeliveryId[delivery.id];
          const aisEta = livePos ? formatDateTime(livePos.eta) : '';
          const aisDwell = livePos ? durationSince(livePos.nav_status_since) : '';
          const aisStatusLabel = livePos && livePos.nav_status
            ? (aisDwell ? `${livePos.nav_status} ${aisDwell}` : livePos.nav_status)
            : '';

          if (isHidden) return null;

          const cardWidgets = getWidgetConfig(delivery.id);
          const visibleWidgets = cardWidgets.filter(w => w.visible).sort((a, b) => a.order - b.order);
          const visibleWidgetIds = visibleWidgets.map(w => w.id);

          return (
            <div
              key={delivery.id}
              className={`so-card ${isExpanded ? 'so-card-expanded' : ''} ${isHighlighted ? 'so-card-highlighted' : ''}`}
              style={{ '--card-accent': accent.border, '--card-gradient': accent.gradient, '--card-light': accent.light }}
              onMouseEnter={() => !expandedDeliveryId && setHoveredVesselKey(cardKey)}
              onMouseLeave={() => !expandedDeliveryId && setHoveredVesselKey(null)}
            >
              <div className="so-card-accent" style={{ background: accent.gradient }} />

              <div
                className="so-card-face"
                onClick={() => {
                  setFlyTrigger({ key: cardKey, ts: Date.now() });
                  handleToggleExpand(delivery.id);
                }}
              >
                <div className="so-card-title-row">
                  <span className={`so-card-status ${delivery.status === 'ACTIVE' ? 'so-card-status-active' : 'so-card-status-draft'}`}>
                    {delivery.status === 'ACTIVE' ? 'Active' : 'Draft'}
                  </span>
                  <h4 className="so-card-name">
                    {delivery.display_name || delivery.delivery_unique_number}
                  </h4>
                  <span className="so-card-id">{delivery.delivery_unique_number}</span>
                </div>

                <div className="so-card-metrics">
                  <div className="so-card-metric">
                    <FontAwesomeIcon icon={faShip} className="so-card-metric-icon" style={{ color: accent.border }} />
                    <div className="so-card-metric-content">
                      <span className="so-card-metric-label">Vessel</span>
                      <span className="so-card-metric-value">
                        {delivery.vessel_name || '--'}
                        {delivery.vessel_imo_number ? (
                          <>
                            {' '}
                            <VesselComplianceBadge imo={delivery.vessel_imo_number} />
                            <VesselRiskChips imo={delivery.vessel_imo_number} />
                          </>
                        ) : null}
                      </span>
                    </div>
                  </div>
                  <div className="so-card-metric">
                    <FontAwesomeIcon icon={faRoute} className="so-card-metric-icon" style={{ color: accent.border }} />
                    <div className="so-card-metric-content">
                      <span className="so-card-metric-label">Route</span>
                      <span className="so-card-metric-value">
                        {delivery.loading_port_location && delivery.discharging_port_location
                          ? `${delivery.loading_port_location} - ${delivery.discharging_port_location}`
                          : '--'}
                      </span>
                    </div>
                  </div>
                  <div className="so-card-metric">
                    <FontAwesomeIcon icon={faAnchor} className="so-card-metric-icon" style={{ color: accent.border }} />
                    <div className="so-card-metric-content">
                      <span className="so-card-metric-label">Quantity</span>
                      <span className="so-card-metric-value">
                        {delivery.delivery_product_quantity ? `${formatAmount(formatFigure(delivery.delivery_product_quantity, 0, QUANTITY_DECIMALS))} ${delivery.delivery_product_unit || 'MT'}` : '--'}
                      </span>
                    </div>
                  </div>
                  <div className="so-card-metric">
                    <FontAwesomeIcon icon={faCalendarAlt} className="so-card-metric-icon" style={{ color: accent.border }} />
                    <div className="so-card-metric-content">
                      <span className="so-card-metric-label">
                        {delivery.commodity_type === 'fuels' ? 'BDN Date' : 'BL Date'}
                      </span>
                      <span className="so-card-metric-value">{formatDate(delivery.bill_of_lading_date)}</span>
                    </div>
                  </div>
                  <div className="so-card-metric">
                    <FontAwesomeIcon icon={faCalendarAlt} className="so-card-metric-icon" style={{ color: accent.border }} />
                    <div className="so-card-metric-content">
                      <span className="so-card-metric-label">{aisEta ? 'Planned ETA' : 'ETA'}</span>
                      <span className="so-card-metric-value">{formatDate(delivery.delivery_estimated_time_of_arrival)}</span>
                    </div>
                  </div>
                  {aisEta && (
                    <div className="so-card-metric">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="so-card-metric-icon" style={{ color: accent.border }} />
                      <div className="so-card-metric-content">
                        <span className="so-card-metric-label">Live AIS ETA</span>
                        <span className="so-card-metric-value">
                          {aisEta}
                          {livePos.destination ? <span className="so-card-metric-sub"> → {livePos.destination}</span> : null}
                        </span>
                      </div>
                    </div>
                  )}
                  {aisStatusLabel && (
                    <div className="so-card-metric">
                      <FontAwesomeIcon icon={faShip} className="so-card-metric-icon" style={{ color: accent.border }} />
                      <div className="so-card-metric-content">
                        <span className="so-card-metric-label">AIS Status</span>
                        <span className="so-card-metric-value">{aisStatusLabel}</span>
                      </div>
                    </div>
                  )}
                </div>

                {!isExpanded && (
                  <div className="so-card-footer">
                    <span className="so-card-footer-hint">Click to expand</span>
                    <button
                      className="so-card-open-btn"
                      style={{ background: accent.border }}
                      onClick={(e) => { e.stopPropagation(); history.push(`/deliveries/${delivery.id}`); }}
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} /> Open Delivery
                    </button>
                  </div>
                )}
              </div>

              {isExpanded && (
                <div className="so-card-expanded-body">
                  <div className="so-card-toolbar" style={{ borderColor: accent.light }}>
                    <div className="so-card-toolbar-left">
                      <button
                        className="so-card-back-btn"
                        onClick={() => { setExpandedDeliveryId(null); setIsEditMode(false); setShowPalette(false); }}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} /> All Deliveries
                      </button>
                      <button
                        className="so-card-open-btn"
                        style={{ background: accent.border }}
                        onClick={() => history.push(`/deliveries/${delivery.id}`)}
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} /> Open Delivery
                      </button>
                    </div>
                    <div className="so-card-toolbar-right">
                      {!isEditMode ? (
                        <button
                          className="so-card-toolbar-btn"
                          style={{ borderColor: accent.border, color: accent.border }}
                          onClick={() => { setIsEditMode(true); setShowPalette(true); }}
                        >
                          <FontAwesomeIcon icon={faSlidersH} />
                          <span>Customize</span>
                        </button>
                      ) : (
                        <button
                          className="so-card-toolbar-btn so-card-toolbar-btn-save"
                          onClick={() => handleSaveAndClose(delivery.id)}
                          disabled={layoutSaving}
                        >
                          <FontAwesomeIcon icon={layoutSaving ? faSync : faSave} spin={layoutSaving} />
                          <span>{layoutSaving ? 'Saving...' : 'Save & Close'}</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="so-card-content-area">
                    {(fullData.vessel_imo_number || delivery.vessel_imo_number) ? (
                      <VesselIntelligencePanel imo={fullData.vessel_imo_number || delivery.vessel_imo_number} />
                    ) : null}
                    {showPalette && isEditMode && (
                      <div className="so-card-palette-inline">
                        <WidgetPalette
                          widgetConfig={cardWidgets}
                          onToggleWidget={(widgetId) => handleToggleWidget(delivery.id, widgetId)}
                          onClose={() => setShowPalette(false)}
                        />
                      </div>
                    )}

                    <div className={`so-card-widgets ${showPalette && isEditMode ? 'so-card-widgets-with-palette' : ''}`}>
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(event) => handleDragEnd(delivery.id, event)}>
                        <SortableContext items={visibleWidgetIds} strategy={rectSortingStrategy}>
                          <div className="so-widget-grid">
                            {visibleWidgets.map(w => (
                              <DeliveryWidgetCard
                                key={w.id}
                                widgetId={w.id}
                                delivery={fullData}
                                isEditMode={isEditMode}
                                accentColor={accent.border}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {deliveries.length === 0 && (
          <div className="so-empty-state">
            <FontAwesomeIcon icon={faShip} className="so-empty-icon" />
            <h3>No Deliveries Yet</h3>
            <p>Deliveries for this quarter will appear here with their vessel tracking and details.</p>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default ShipmentOverviewTab;
